import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '../components/Icon';
import { faCartPlus, faCalendarCheck, faCircleCheck, faTriangleExclamation, faShieldHalved, faScrewdriverWrench, faTruckFast, faMoneyBillWave, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAddToCart, useSettings } from '../lib/store';
import { useFetch } from '../lib/useFetch';
import { useI18n } from '../lib/i18n';
import { categoryHref, usageLabel } from '../lib/format';
import { ProductBadges, ProductGrid } from '../components/ProductCard';
import { Crumbs, Empty, QtyInput, SectionHead } from '../components/ui';
import { imgUrl, responsive } from '../lib/img';

export default function Product() {
  const { slug } = useParams();
  const { settings: s, money } = useSettings();
  const addToCart = useAddToCart();
  const { t, pick } = useI18n();
  const { data, loading, error } = useFetch(`/api/products/${encodeURIComponent(slug)}`);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  useEffect(() => { setImg(0); setQty(1); }, [slug]);
  useEffect(() => { if (data) document.title = `${pick(data.product, 'name')} – ${pick(s, 'storeName')}`; }, [data, s.storeName]);

  if (loading) return <div className="shell grid gap-12 py-9 lg:grid-cols-2"><div className="skeleton aspect-square" /><div className="skeleton h-96" /></div>;
  if (error || !data) {
    return <div className="shell py-16"><Empty>{t('Product not found.')}<br /><Link to="/shop" className="btn btn-primary mt-5">{t('Browse products')}</Link></Empty></div>;
  }

  const { product: p, related } = data;
  const cat = s.categories.find((c) => c.key === p.category);
  const isService = p.category === 'installation';
  const images = p.images.length ? p.images : ['/uploads/cable.svg'];
  const maxQty = isService ? 50 : p.stock;
  const wa = `https://wa.me/${s.whatsapp}?text=${encodeURIComponent(t('Hi, I am interested in: {name} ({sku}) – {url}', { name: pick(p, 'name'), sku: p.sku, url: window.location.href }))}`;
  const attrs = [
    [t('Type'), p.currentType], [t('Power'), p.powerKw && `${p.powerKw} kW`], [t('Connector'), p.connector],
    [t('Use'), p.usage.map((u) => t(usageLabel(u))).join(t(', '))], [t('Warranty'), p.warranty && t(p.warranty)], [t('SKU'), p.sku && <span dir="ltr">{p.sku}</span>],
  ].filter(([, v]) => v);

  let stock = <span className="font-bold text-sale"><FontAwesomeIcon icon={faTriangleExclamation} /> {t('Out of stock – contact us for lead time')}</span>;
  if (isService && p.inStock) stock = <span className="font-bold text-emerald-700"><FontAwesomeIcon icon={faCircleCheck} /> {t('Available – we will call to schedule')}</span>;
  else if (p.stock > 5) stock = <span className="font-bold text-emerald-700"><FontAwesomeIcon icon={faCircleCheck} /> {t('In stock')}</span>;
  else if (p.stock > 0) stock = <span className="font-bold text-amber-600"><FontAwesomeIcon icon={faTriangleExclamation} /> {t('Only {n} left in stock', { n: p.stock })}</span>;

  return (
    <>
      <div className="shell">
        <div className="grid gap-10 pt-9 pb-14 lg:grid-cols-2 lg:gap-12">
          <div className="min-w-0">
            <div className="aspect-square overflow-hidden rounded-2xl border border-slate-200 bg-soft">
              <img {...responsive(images[img], '(min-width: 1024px) 45vw, 92vw', 1280)} width="800" height="800" fetchPriority="high" alt={pick(p, 'name')} className="size-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2.5 overflow-x-auto">
                {images.map((src, i) => (
                  <button key={src} onClick={() => setImg(i)} aria-label={t('Image {n}', { n: i + 1 })}
                    className={`size-18 shrink-0 cursor-pointer overflow-hidden rounded-xl border-2 bg-soft ${i === img ? 'border-brand-500' : 'border-transparent'}`}>
                    <img src={imgUrl(src, 160)} alt="" loading="lazy" width="80" height="80" className="size-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="min-w-0">
            <Crumbs items={[[t('Home'), '/'], [(cat && pick(cat, 'name')) || '', categoryHref(p.category)]]} />
            <ProductBadges p={p} className="mt-2" />
            <h1 className="mt-2 mb-3 text-3xl leading-tight font-extrabold"><bdi>{pick(p, 'name')}</bdi></h1>
            <p className="text-slate-500"><bdi>{pick(p, 'shortDescription')}</bdi></p>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-3xl font-extrabold">{money(p.salePrice)}</span>
              {p.discountPercent > 0 && <>
                <span className="text-slate-500 line-through">{money(p.price)}</span>
                <span className="text-sm font-bold text-sale">{t('You save {amount}', { amount: money(p.price - p.salePrice) })}</span>
              </>}
            </div>
            <p className="mt-1 text-[13px] text-slate-500">
              {s.vatPercent ? t('Excl. {vat}% VAT', { vat: s.vatPercent }) : ''}{isService ? ` · ${t('Final price confirmed after site survey')}` : ''}
            </p>

            <dl className="my-6 grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2.5">
              {attrs.map(([k, v]) => (
                <div key={k} className="rounded-xl bg-soft px-3 py-2.5">
                  <dt className="text-[11px] tracking-wider text-slate-500 uppercase">{k}</dt>
                  <dd className="text-sm font-bold"><bdi>{v}</bdi></dd>
                </div>
              ))}
            </dl>

            <p className="text-sm">{stock}</p>
            <div className="my-5 flex flex-wrap items-center gap-3">
              <QtyInput value={qty} onChange={setQty} max={Math.max(1, maxQty)} />
              <button className="btn btn-primary" disabled={!p.inStock} onClick={() => addToCart(p, qty)}>
                <FontAwesomeIcon icon={isService ? faCalendarCheck : faCartPlus} /> {isService ? t('Book this service') : t('Add to cart')}
              </button>
              <a className="btn btn-whatsapp" href={wa} target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faWhatsapp} className="text-lg" /> WhatsApp</a>
            </div>
            {p.category === 'chargers' && (
              <p className="text-sm text-slate-500">{t('Need it installed?')} <Link to="/installation" className="font-bold text-brand-600 hover:underline">{t('Add an installation package')} <FontAwesomeIcon icon={faArrowRight} /></Link></p>
            )}
            <ul className="mt-6 grid gap-2.5 text-[13px] text-slate-600 sm:grid-cols-2">
              {[[faShieldHalved, 'Genuine products with warranty'], [faScrewdriverWrench, 'Certified installation available'], [faTruckFast, 'UAE-wide delivery'], [faMoneyBillWave, 'Cash on delivery / bank transfer']].map(([icon, label]) => (
                <li key={label} className="flex items-center gap-2"><FontAwesomeIcon icon={icon} className="w-4 text-accent" />{t(label)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid gap-10 border-t border-slate-200 pt-8 pb-14 md:grid-cols-2">
          <div>
            <h2 className="mb-3 text-lg font-bold">{t('Description')}</h2>
            <p className="whitespace-pre-line text-slate-700"><bdi>{pick(p, 'description') || pick(p, 'shortDescription')}</bdi></p>
          </div>
          {p.specs.length > 0 && (
            <div>
              <h2 className="mb-3 text-lg font-bold">{t('Specifications')}</h2>
              <table className="w-full text-sm">
                <tbody>
                  {p.specs.map((x) => (
                    <tr key={x.label} className="border-b border-slate-200 last:border-0">
                      <td className="w-2/5 px-3.5 py-3 align-top font-semibold text-slate-500"><bdi>{pick(x, 'label')}</bdi></td>
                      <td className="px-3.5 py-3"><bdi>{pick(x, 'value')}</bdi></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="bg-soft py-14">
          <div className="shell">
            <SectionHead title={t('You may also like')} />
            <ProductGrid products={related} />
          </div>
        </section>
      )}
    </>
  );
}
